/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import {
  GDSStyleObject,
  Icon,
  ParagraphText,
  StackLayout,
  TextAction,
} from '@leagueplatform/genesis-core';
import { useStaticAsset } from '@leagueplatform/asset-config';
import { getPartnerAccessToken } from '@leagueplatform/sso-util';
import { FORMS_LIBRARY_ASSET_KEYS } from '../../types/forms-library.types';
import { formsLibrary } from '../../forms-library.config';
import { ErrorModal } from '../forms/layout/errorModal';
import { RemoveAttachmentWarningModal } from '../forms/layout/removeAttachmentWarningModal';
import { RemoveAttachmentSuccessModal } from '../forms/layout/removeAttachmentSuccessModal';
import { AttachmentLimitExceededModal } from '../forms/layout/attachmentLimitExceededModal';

export function AttachmentReusableComponent(props: any) {
  const {
    displayText,
    acceptTypes,
    onUpdate,
    attachments,
    serviceIndex,
    setIsAttaachmentLoading,
  } = props;
  const [fileData, setFileData] = useState<any>([]);
  const [filesCount, setFilesCount] = useState(0);
  const [filesTotalSize, setFilesTotalSize] = useState(0);
  const [filesSizeArray, setFilesSizeArray] = useState<any>([]);
  const [errMsg, setErrMsg] = useState(null);
  const [indexToRemove, setIndexToRemove] = useState(0);
  const [isLimitExceededModal, setIsLimitExceededModal] = useState(false);
  const [isRemoveAttachmentWarningModal, setIsRemoveAttachmentWarningModal] =
    useState(false);
  const [isRemoveAttachmentSuccessModal, setIsRemoveAttachmentSuccessModal] =
    useState(false);

  const attachmentIcon = useStaticAsset(
    FORMS_LIBRARY_ASSET_KEYS.ATTACHMENT_ICON,
  );
  const pdfIcon = useStaticAsset(FORMS_LIBRARY_ASSET_KEYS.PDF_ICON);
  const docIcon = useStaticAsset(FORMS_LIBRARY_ASSET_KEYS.DOC_ICON);
  const imgIcon = useStaticAsset(FORMS_LIBRARY_ASSET_KEYS.IMAGE_ICON);
  const closeIcon = useStaticAsset(FORMS_LIBRARY_ASSET_KEYS.CLOSE_ICON);

  const config = formsLibrary.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;

  useEffect(() => {
    if (attachments.length === 0) {
      setFileData([]);
      setFilesCount(0);
      setFilesSizeArray([]);
      setFilesTotalSize(0);
    }
    if (attachments.length > 0) {
      setFileData(attachments);
      setFilesCount(attachments.length);
      const attachmentSize = attachments.map((att: any) => att.size);
      setFilesSizeArray(attachmentSize);
      const totalSize = attachmentSize.reduce(
        (att1: any, att2: any) => att1 + att2,
      );
      setFilesTotalSize(totalSize);
    }
  }, [attachments]);

  const testFileApi = async (file: any) => {
    setIsAttaachmentLoading(true);
    const form = new FormData();
    form.append('file', file);

    const token = await getPartnerAccessToken('highmark');

    const url = `${HIGHMARK_API_URL}/digital/v1/members/messages/attachment`;
    const options = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(ubkValue && { brandUbk: ubkValue }),
      },
      body: form,
    };

    try {
      return await fetch(url, options)
        .then((response) => {
          setIsAttaachmentLoading(false);
          if (!response.ok) {
            throw new Error(
              `This is an HTTP error: The status is ${response.status}`,
            );
          }
          return response.json();
        })
        .then((data) => data);
    } catch (error) {
      const errObj = {
        errMsg: error,
      };
      return errObj;
    }
  };

  const fileChanged = (e: any) => {
    const file = e.target.files[0];
    const fileNameArray = file.name.split('.');
    file.newName = `${fileNameArray[0]}-${Date.now()}.${
      fileNameArray[fileNameArray.length - 1]
    }`;

    const fileDataCopy = [...fileData];
    const sizeCopy = [...filesSizeArray];

    if (e.target.value) {
      const newFileSize = file.size;
      const newTotalSize = filesTotalSize + newFileSize;
      if (newFileSize > 10485760) {
        setIsLimitExceededModal(true);
        e.target.value = '';
      } else if (newTotalSize > 10485760) {
        setIsLimitExceededModal(true);
        e.target.value = '';
      } else {
        testFileApi(file).then((result: any) => {
          const fileApiData = result?.attachmentPath;
          const fileErrorData = result?.errMsg;
          if (fileApiData) {
            const fileObj = {
              name: file.name,
              attachmentPath: fileApiData,
              size: file.size,
              newName: file.newName,
              type: file.type,
            };
            fileDataCopy.push(fileObj);
            setFilesCount(filesCount + 1);
            setFilesTotalSize(newTotalSize);
            sizeCopy.push(newFileSize);
            setFilesSizeArray(sizeCopy);
            setFileData(fileDataCopy);
            onUpdate(fileDataCopy);
          }
          if (fileErrorData) {
            setErrMsg(fileErrorData);
          }
        });
      }
    }
  };

  const triggerToSelectFile = () => {
    const fileBtn = document.getElementById(`attachment${serviceIndex}`);
    fileBtn?.click();
  };

  const deleteAttachment = (index: any) => {
    setIndexToRemove(index);
    setIsRemoveAttachmentWarningModal(true);
  };

  const removeFile = (key: string) => {
    if (key === 'Yes') {
      const fileDataCopy = [...fileData];
      const sizeCopy = [...filesSizeArray];
      fileDataCopy.splice(indexToRemove, 1);
      const fileSizeToRemove = sizeCopy.splice(indexToRemove, 1);
      const newFileSize = filesTotalSize - fileSizeToRemove[0];
      setFilesTotalSize(newFileSize);
      setFileData(fileDataCopy);
      onUpdate(fileDataCopy);
      setFilesCount(filesCount - 1);
      setFilesSizeArray(sizeCopy);
      setIsRemoveAttachmentSuccessModal(true);
    }
    setIsRemoveAttachmentWarningModal(false);
  };

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '590px',
        } as GDSStyleObject
      }
    >
      {isLimitExceededModal && (
        <AttachmentLimitExceededModal
          onClose={() => setIsLimitExceededModal(false)}
          openExceededModal={isLimitExceededModal}
        />
      )}
      {isRemoveAttachmentWarningModal && (
        <RemoveAttachmentWarningModal
          onClose={(val: string) => removeFile(val)}
          openWarningModal={isRemoveAttachmentWarningModal}
        />
      )}
      {isRemoveAttachmentSuccessModal && (
        <RemoveAttachmentSuccessModal
          onClose={() => setIsRemoveAttachmentSuccessModal(false)}
          openSuccessModal={isRemoveAttachmentSuccessModal}
        />
      )}
      <ParagraphText
        css={
          {
            maxWidth: '590px',
          } as GDSStyleObject
        }
      >
        {filesTotalSize < 10485760 && fileData.length < 10 && (
          <>
            <input
              type="file"
              accept={acceptTypes.join(', ')}
              id={`attachment${serviceIndex}`}
              data-testid="fileUpload"
              onChange={fileChanged}
              onClick={(e: any) => {
                e.target.value = null;
              }}
              style={{ width: '0' }}
              hidden
            />
            <TextAction
              icon={attachmentIcon as string}
              size={{
                '@initial': 'large',
                '@mobile': 'medium',
                '@tablet': 'large',
              }}
              css={
                {
                  maxWidth: '590px',
                } as GDSStyleObject
              }
              as="button"
              onClick={triggerToSelectFile}
            >
              {displayText}
            </TextAction>
            <p>
              You may attach a file up to 10 MB. The accepted file types are
              .pdf, .doc, .png, .gif, or .jpg.
            </p>
          </>
        )}
        {fileData.length > 0 &&
          fileData.map((file: any, index: any) => (
            <TextAction
              css={
                {
                  maxWidth: '590px',
                } as GDSStyleObject
              }
            >
              <TextAction
                css={
                  {
                    paddingBottom: '15px',
                    '@mobile': { paddingRight: '50px' },
                    '@desktop': { width: '590px' },
                    '@laptop': { width: '590px' },
                    '@mobileLandscape': { width: '590px' },
                    '@tablet': { width: '590px' },
                  } as GDSStyleObject
                }
              >
                <span>
                  <img
                    src={
                      // eslint-disable-next-line no-nested-ternary
                      file.type === 'application/pdf'
                        ? (pdfIcon as string)
                        : file.type === 'application/msword'
                        ? (docIcon as string)
                        : (imgIcon as string)
                    }
                    alt="Visual Icon"
                    height="15px"
                    style={{
                      marginRight: '8px',
                    }}
                  />
                  {file.name}
                </span>
              </TextAction>
              <TextAction
                data-testid="deleteFile"
                onClick={() => deleteAttachment(index)}
                size={{
                  '@initial': 'large',
                  '@mobile': 'medium',
                  '@tablet': 'large',
                }}
              >
                <Icon icon={closeIcon as string} size="17px" />
              </TextAction>
            </TextAction>
          ))}
      </ParagraphText>

      {errMsg && <ErrorModal onClose={() => setErrMsg(null)} />}
    </StackLayout>
  );
}
